import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

export default function EnrollmentPanel({ classData, onClose, onUpdated }) {
  useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);

  const fetchEnrollments = useCallback(async () => {
    if (!classData?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/classes/${classData?.id}/enrollments`);
      setEnrollments(data || []);
    } catch (err) {
      setError(err?.message || "Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  }, [classData?.id]);

  const fetchAvailableMembers = useCallback(async () => {
    try {
      const { data } = await api.get('/members', { params: { status: 'active', limit: 2000 } });
      setMembers(data || []);
    } catch (err) {
      console.error("Failed to load members:", err);
    }
  }, []);

  useEffect(() => {
    fetchEnrollments();
    fetchAvailableMembers();
  }, [fetchEnrollments, fetchAvailableMembers]);

  const enrolledMemberIds = enrollments?.map(e => e?.member_id) || [];
  const availableToEnroll = members?.filter(m => !enrolledMemberIds?.includes(m?.id));

  const enrolledCount = enrollments?.filter(e => e?.status === "enrolled")?.length || 0;
  const waitlistedCount = enrollments?.filter(e => e?.status === "waitlisted")?.length || 0;
  const spotsRemaining = (classData?.max_capacity || 0) - enrolledCount;
  const isFull = spotsRemaining <= 0;

  const handleEnroll = async () => {
    if (!selectedMemberId) return;
    setEnrolling(true);
    setError("");
    try {
      await api.post(`/classes/${classData?.id}/enrollments`, { member_id: selectedMemberId });
      setSelectedMemberId("");
      await fetchEnrollments();
      onUpdated?.();
    } catch (err) {
      setError(err?.message || "Failed to enroll member");
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemove = async (memberId) => {
    setRemovingId(memberId);
    try {
      await api.delete(`/classes/${classData?.id}/enrollments/${memberId}`);
      await fetchEnrollments();
      onUpdated?.();
    } catch (err) {
      setError(err?.message || "Failed to remove enrollment");
    } finally {
      setRemovingId(null);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      Yoga: "bg-purple-100 text-purple-700",
      HIIT: "bg-red-100 text-red-700",
      Strength: "bg-blue-100 text-blue-700",
      Cardio: "bg-orange-100 text-orange-700",
      Pilates: "bg-pink-100 text-pink-700",
      Zumba: "bg-yellow-100 text-yellow-700",
      CrossFit: "bg-green-100 text-green-700",
      Spinning: "bg-cyan-100 text-cyan-700",
      Boxing: "bg-gray-100 text-gray-700",
      General: "bg-indigo-100 text-indigo-700",
    };
    return colors?.[type] || "bg-indigo-100 text-indigo-700";
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--color-card)] h-full flex flex-col shadow-2xl border-l border-[var(--color-border)] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(classData?.class_type)}`}>
                {classData?.class_type}
              </span>
              <span className="text-xs text-[var(--color-muted-foreground)]">{classData?.recurrence}</span>
            </div>
            <h2 className="text-base font-bold text-[var(--color-foreground)] truncate">{classData?.name}</h2>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">
              {classData?.start_time?.slice(0, 5)} – {classData?.end_time?.slice(0, 5)} · {classData?.start_date}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors flex-shrink-0">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
          <div className="text-center">
            <p className="text-lg font-bold text-[var(--color-foreground)]">{enrolledCount}</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">Enrolled</p>
          </div>
          <div className="text-center">
            <p className={`text-lg font-bold ${isFull ? "text-red-500" : "text-green-600"}`}>{spotsRemaining}</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">Spots Left</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-orange-500">{waitlistedCount}</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">Waitlisted</p>
          </div>
        </div>

        {/* Enroll Member */}
        <div className="px-5 py-3 border-b border-[var(--color-border)]">
          <p className="text-xs font-semibold text-[var(--color-foreground)] mb-2">
            {isFull ? "Add to Waitlist" : "Enroll Member"}
          </p>
          {error && (
            <div className="flex items-center gap-1.5 p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 mb-2">
              <Icon name="AlertCircle" size={12} color="#ef4444" />
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <select
              value={selectedMemberId}
              onChange={e => setSelectedMemberId(e?.target?.value)}
              className="flex-1 px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="">Select member...</option>
              {availableToEnroll?.map(m => (
                <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>
              ))}
            </select>
            <button
              onClick={handleEnroll}
              disabled={!selectedMemberId || enrolling}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {enrolling ? <Icon name="Loader2" size={14} color="white" /> : <Icon name="UserPlus" size={14} color="white" />}
              {isFull ? "Waitlist" : "Enroll"}
            </button>
          </div>
          {isFull && (
            <p className="text-xs text-orange-600 mt-1.5 flex items-center gap-1">
              <Icon name="AlertTriangle" size={11} color="#ea580c" />
              Class is at capacity. New enrollments will be waitlisted.
            </p>
          )}
        </div>

        {/* Enrollments List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <p className="text-xs font-semibold text-[var(--color-foreground)] mb-3">
            Enrolled Members ({enrollments?.length})
          </p>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="h-14 bg-[var(--color-muted)] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : enrollments?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
                <Icon name="Users" size={20} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">No enrollments yet</p>
              <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Enroll members using the form above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {enrollments?.map(enrollment => (
                <div
                  key={enrollment?.id}
                  className="flex items-center justify-between p-3 bg-[var(--color-muted)]/40 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary)]/30 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[var(--color-primary)]">
                        {enrollment?.member_name?.split(' ')?.[0]?.[0]}{enrollment?.member_name?.split(' ')?.[1]?.[0]}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--color-foreground)] truncate">
                        {enrollment?.member_name || "Unknown"}
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)] truncate">
                        {enrollment?.phone || "No contact"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {enrollment?.status === "waitlisted" ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Waitlist</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Enrolled</span>
                    )}
                    <button
                      onClick={() => handleRemove(enrollment?.member_id)}
                      disabled={removingId === enrollment?.member_id}
                      className="p-1.5 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                      title="Remove enrollment"
                    >
                      {removingId === enrollment?.member_id ? (
                        <Icon name="Loader2" size={13} color="currentColor" />
                      ) : (
                        <Icon name="UserMinus" size={13} color="currentColor" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
